/**
 * AvatarAllocationContext — debate-scoped, collision-free avatar lookup.
 *
 * Wrap the live council subtree (roundtable stage, present mode, replay
 * surface) with `<AvatarAllocationProvider personas={...}>`. Inside, any
 * `AgentAvatar` (or other consumer) calls `useResolvedAvatarUrl(persona)`
 * and gets the unique URL produced by `assignUniqueAvatars`.
 *
 * Why a Context?
 *   - The allocator must see ALL personas of a debate at once (uniqueness
 *     is a global property) → can't be computed inside `AgentAvatar`.
 *   - Prop-drilling the URL through every renderer (RoundtableStage,
 *     PresentModeShell, GalleryView, etc.) would be invasive and
 *     error-prone.
 *   - A Context gives every consumer the same Map, recomputed only when
 *     the persona-id set changes (memoised by sorted-id key).
 *
 * Outside the provider, `useResolvedAvatarUrl` falls back to
 * `resolveAvatarUrl` (single-persona deterministic — may collide).
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import type { Persona } from "../../domain/types";
import { assignUniqueAvatars, resolveAvatarUrl } from "../../utils/avatarPool";

const AvatarAllocationContext = createContext<Map<string, string> | null>(null);

interface AvatarAllocationProviderProps {
  /** Council snapshot. Order is irrelevant — the allocator sorts by id. */
  personas: readonly Persona[];
  children: ReactNode;
}

export function AvatarAllocationProvider({
  personas,
  children,
}: AvatarAllocationProviderProps) {
  const allocation = useMemo(() => {
    return assignUniqueAvatars(personas);
    // Recompute only when the persona id set or any backend-stamped
    // avatarUrl actually changes — prevents thrash on every store tick.
  }, [
    personas
      .map((p) => `${p.id}|${p.avatarUrl ?? ""}|${p.gender ?? ""}|${p.honorific ?? ""}`)
      .sort()
      .join("§"),
  ]);

  return (
    <AvatarAllocationContext.Provider value={allocation}>
      {children}
    </AvatarAllocationContext.Provider>
  );
}

/**
 * Resolve the avatar URL for `persona`, preferring the debate-scoped
 * unique allocation when a provider is mounted above. Falls back to the
 * legacy single-shot deterministic resolver otherwise.
 */
export function useResolvedAvatarUrl(persona: Persona): string {
  const allocation = useContext(AvatarAllocationContext);
  if (allocation) {
    const url = allocation.get(persona.id);
    if (url) return url;
  }
  return resolveAvatarUrl(persona);
}
